/*
 * InvoicingPanel — admin view for generating TP invoices from completed trips.
 * P2 scaffold: previews subtotal / dead-mile pay / total from the past N days.
 * "Issue" persists a snapshot to db.tp_invoices and stamps invoice_id on each
 * underlying trip so the same trips aren't billed twice.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { apiClient } from "../../lib/api";
import { confirm } from "../../components/ConfirmModal";
import { Receipt, Loader2, FileText, RefreshCw, Download, Mail, Wallet } from "lucide-react";

const TP = "/tpt";

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export default function InvoicingPanel() {
  const [providers, setProviders] = useState([]);
  const [tpId, setTpId] = useState("");
  const [periodStart, setPeriodStart] = useState(isoDaysAgo(7));
  const [periodEnd, setPeriodEnd] = useState(new Date().toISOString());
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [history, setHistory] = useState([]);
  const [ccBroker, setCcBroker] = useState("");
  const [emailAfterIssue, setEmailAfterIssue] = useState(true);

  const downloadPdf = async (invoiceId) => {
    try {
      const { data } = await apiClient.get(`${TP}/providers/${tpId}/invoices/${invoiceId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoiceId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("PDF download failed"); }
  };

  const resendEmail = async (invoiceId) => {
    try {
      const { data } = await apiClient.post(`${TP}/providers/${tpId}/invoices/${invoiceId}/resend`);
      toast.success(`Queued to ${data.recipient}`);
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resend failed"); }
  };

  useEffect(() => {
    apiClient.get(`${TP}/providers`).then(({ data }) => {
      setProviders(data || []);
      if (!tpId && data && data.length) setTpId(data[0].tp_id);
    }).catch(() => toast.error("Failed to load providers"));
  }, []);

  const loadPreview = useCallback(async () => {
    if (!tpId) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get(`${TP}/providers/${tpId}/invoice/preview`, {
        params: { period_start: periodStart, period_end: periodEnd },
      });
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setLoading(false); }
  }, [tpId, periodStart, periodEnd]);

  const loadHistory = useCallback(async () => {
    if (!tpId) return;
    try {
      const { data } = await apiClient.get(`${TP}/providers/${tpId}/invoices`);
      setHistory(data || []);
    } catch { /* silent */ }
  }, [tpId]);

  const settleInvoice = async (invoiceId) => {
    const ok = await confirm({
      title: "Pay this provider now?",
      body: "This sends a real Stripe transfer to their Connect payout account for the invoice total. This cannot be undone.",
      confirmLabel: "Pay now",
      tone: "primary",
    });
    if (!ok) return;
    try {
      const { data } = await apiClient.post(`${TP}/providers/${tpId}/invoices/${invoiceId}/settle`);
      toast.success(`Paid · transfer ${data.stripe_transfer_id?.slice(0, 12) || ""}`);
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Settlement failed"); }
  };

  useEffect(() => { loadPreview(); loadHistory(); }, [loadPreview, loadHistory]);

  const issue = async () => {
    if (!preview || preview.trip_count === 0) { toast.info("Nothing to invoice"); return; }
    const ok = await confirm({
      title: `Issue invoice for ${preview.tp_name}?`,
      body: `${preview.trip_count} trips · Total $${preview.total.toFixed(2)}.${emailAfterIssue ? " Invoice PDF will be emailed to the TP." : ""} Trips will be marked invoiced.`,
      confirmLabel: "Issue invoice",
      tone: "primary",
    });
    if (!ok) return;
    setIssuing(true);
    try {
      const params = { send_email: emailAfterIssue };
      if (ccBroker.trim()) params.cc_broker_email = ccBroker.trim();
      const { data } = await apiClient.post(`${TP}/providers/${tpId}/invoice/issue`, {
        period_start: periodStart, period_end: periodEnd,
      }, { params });
      toast.success(`Invoice issued · ${data.invoice_id.slice(0, 8)} · $${data.total.toFixed(2)}${data.email_status === "queued" ? " · email queued" : ""}`);
      loadPreview(); loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Issue failed"); }
    finally { setIssuing(false); }
  };

  const setPreset = (days) => {
    setPeriodStart(isoDaysAgo(days));
    setPeriodEnd(new Date().toISOString());
  };

  const dateInput = (val, on) => (
    <input
      type="date"
      value={val ? val.slice(0, 10) : ""}
      onChange={(e) => on(new Date(e.target.value + "T00:00:00Z").toISOString())}
      className="border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"
    />
  );

  const grandTotal = useMemo(() => (preview?.total || 0).toFixed(2), [preview]);

  return (
    <div data-testid="invoicing-panel">
      <div className="flex items-center gap-3 mb-6">
        <Receipt className="w-8 h-8 text-forest"/>
        <div>
          <h2 className="text-xl font-bold text-slate_navy">TP Invoicing</h2>
          <p className="text-sm text-slate-500">Aggregate completed trips per provider into an invoiceable snapshot. Preview any window; issue stamps trips as invoiced.</p>
        </div>
      </div>

      <div className="bg-white border border-[#E2E0D8] rounded-xl p-4 mb-6">
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <label className="block">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Provider</div>
            <select value={tpId} onChange={(e) => setTpId(e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="invoice-tp-select">
              {providers.map(p => <option key={p.tp_id} value={p.tp_id}>{p.name}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Period start</div>
            {dateInput(periodStart, setPeriodStart)}
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Period end</div>
            {dateInput(periodEnd, setPeriodEnd)}
          </label>
          <div className="flex items-center gap-2">
            <button onClick={loadPreview} disabled={loading} className="inline-flex items-center gap-1 border border-[#E2E0D8] px-3 py-2 rounded-md text-sm" data-testid="invoice-refresh">
              {loading ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>} Refresh
            </button>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          {[{ n: 7, l: "Last 7d" }, { n: 14, l: "Last 14d" }, { n: 30, l: "Last 30d" }].map(({ n, l }) => (
            <button key={n} onClick={() => setPreset(n)} className="text-[11px] px-2 py-1 rounded-md border border-[#E2E0D8] text-slate-600 hover:bg-slate-50">{l}</button>
          ))}
        </div>
      </div>

      {preview && (
        <div className="bg-white border border-[#E2E0D8] rounded-xl overflow-hidden mb-6" data-testid="invoice-preview">
          <div className="p-4 border-b border-[#E9EAE4] flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm text-slate-500">Invoice preview · {preview.tp_name}</div>
              <div className="font-bold text-slate_navy text-lg">{preview.trip_count} trip(s) · ${grandTotal}</div>
              <div className="text-[11px] text-slate-500">{preview.period_start.slice(0,10)} → {preview.period_end.slice(0,10)}</div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-slate-600" data-testid="email-after-issue-label">
                <input type="checkbox" checked={emailAfterIssue} onChange={(e) => setEmailAfterIssue(e.target.checked)} data-testid="email-after-issue-toggle"/>
                Email PDF to TP on issue
              </label>
              <input
                type="email"
                value={ccBroker}
                onChange={(e) => setCcBroker(e.target.value)}
                placeholder="CC broker email (optional)"
                className="border border-[#E2E0D8] rounded-md px-3 py-2 text-xs w-56"
                data-testid="cc-broker-email"
              />
              <button onClick={issue} disabled={issuing || preview.trip_count === 0} className="inline-flex items-center gap-2 bg-forest text-white px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50" data-testid="invoice-issue-btn">
                {issuing ? <Loader2 size={14} className="animate-spin"/> : <FileText size={14}/>} Issue invoice
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 divide-x divide-[#E9EAE4] text-center">
            <div className="p-3"><div className="text-[10px] uppercase text-slate-500">Subtotal</div><div className="font-semibold">${preview.subtotal.toFixed(2)}</div></div>
            <div className="p-3"><div className="text-[10px] uppercase text-slate-500">Dead miles</div><div className="font-semibold">${preview.dead_miles_pay.toFixed(2)}</div></div>
            <div className="p-3"><div className="text-[10px] uppercase text-slate-500">Total</div><div className="font-bold text-forest">${grandTotal}</div></div>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="text-left px-3 py-2">Trip</th>
                <th className="text-left px-3 py-2">Rider</th>
                <th className="text-left px-3 py-2">Driver</th>
                <th className="text-right px-3 py-2">Miles</th>
                <th className="text-right px-3 py-2">Fare</th>
                <th className="text-right px-3 py-2">Dead miles</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1EFE8]">
              {preview.line_items.map((li) => (
                <tr key={li.trip_id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-slate-500">{li.trip_id.slice(0, 8)}</td>
                  <td className="px-3 py-2">{li.rider_name || "—"}</td>
                  <td className="px-3 py-2">{li.driver || "—"}</td>
                  <td className="px-3 py-2 text-right">{li.actual_miles.toFixed(1)}</td>
                  <td className="px-3 py-2 text-right">${li.fare.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">${li.dead_mile_pay.toFixed(2)}</td>
                </tr>
              ))}
              {preview.line_items.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No completed trips in this window.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-[#E2E0D8] rounded-xl p-4">
        <div className="text-sm font-semibold text-slate_navy mb-3">Issued invoices ({history.length})</div>
        {history.length === 0 ? (
          <div className="text-xs text-slate-500">No invoices issued yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-slate-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="text-left px-2 py-1">Invoice</th>
                <th className="text-left px-2 py-1">Period</th>
                <th className="text-right px-2 py-1">Trips</th>
                <th className="text-right px-2 py-1">Total</th>
                <th className="text-left px-2 py-1">Email</th>
                <th className="text-left px-2 py-1">Paid</th>
                <th className="text-left px-2 py-1">Issued</th>
                <th className="text-right px-2 py-1">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1EFE8]">
              {history.map((inv) => (
                <tr key={inv.invoice_id} className="hover:bg-slate-50" data-testid={`invoice-row-${inv.invoice_id}`}>
                  <td className="px-2 py-1 font-mono text-slate-500">{inv.invoice_id.slice(0, 8)}</td>
                  <td className="px-2 py-1">{inv.period_start.slice(0,10)} → {inv.period_end.slice(0,10)}</td>
                  <td className="px-2 py-1 text-right">{inv.trip_count}</td>
                  <td className="px-2 py-1 text-right font-semibold">${inv.total.toFixed(2)}</td>
                  <td className="px-2 py-1">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      inv.email_status === "sent" ? "bg-green-100 text-green-800" :
                      inv.email_status === "queued" ? "bg-amber-100 text-amber-800" :
                      inv.email_status === "failed" ? "bg-red-100 text-red-800" :
                      "bg-slate-100 text-slate-600"
                    }`}>
                      {inv.email_status || "—"}
                    </span>
                    {inv.email_recipient && <div className="text-[10px] text-slate-500">{inv.email_recipient}</div>}
                  </td>
                  <td className="px-2 py-1">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      inv.status === "paid" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"
                    }`} data-testid={`invoice-paid-status-${inv.invoice_id}`}>
                      {inv.status === "paid" ? "paid" : "unpaid"}
                    </span>
                    {inv.stripe_transfer_id && <div className="text-[10px] text-slate-500 font-mono">{inv.stripe_transfer_id.slice(0, 14)}</div>}
                  </td>
                  <td className="px-2 py-1 text-slate-500">{new Date(inv.issued_at).toLocaleString()}</td>
                  <td className="px-2 py-1 text-right space-x-1">
                    <button
                      onClick={() => downloadPdf(inv.invoice_id)}
                      className="inline-flex items-center gap-1 border border-[#E2E0D8] px-2 py-1 rounded-md hover:bg-slate-50 text-[11px]"
                      data-testid={`invoice-pdf-${inv.invoice_id}`}
                      title="Download PDF"
                    >
                      <Download size={11}/> PDF
                    </button>
                    <button
                      onClick={() => resendEmail(inv.invoice_id)}
                      className="inline-flex items-center gap-1 border border-[#E2E0D8] px-2 py-1 rounded-md hover:bg-slate-50 text-[11px]"
                      data-testid={`invoice-resend-${inv.invoice_id}`}
                      title="Resend email"
                    >
                      <Mail size={11}/> Resend
                    </button>
                    {inv.status !== "paid" && (
                      <button
                        onClick={() => settleInvoice(inv.invoice_id)}
                        className="inline-flex items-center gap-1 bg-forest text-white px-2 py-1 rounded-md hover:opacity-90 text-[11px] font-semibold"
                        data-testid={`invoice-settle-${inv.invoice_id}`}
                        title="Pay via Stripe Connect transfer"
                      >
                        <Wallet size={11}/> Pay now
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <TPPayoutOnboardingCard tpId={tpId} />
    </div>
  );
}

function TPPayoutOnboardingCard({ tpId }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!tpId) return;
    try {
      const { data } = await apiClient.get(`${TP}/providers/${tpId}/payouts/status`);
      setStatus(data);
    } catch { setStatus(null); }
  }, [tpId]);
  useEffect(() => { load(); }, [load]);

  const sendOnboardingLink = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`${TP}/providers/${tpId}/payouts/onboarding-link`);
      await navigator.clipboard?.writeText(data.url).catch(() => {});
      toast.success("Onboarding link ready — copied to clipboard, send it to the TP.");
      window.open(data.url, "_blank");
      setTimeout(load, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create onboarding link"); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  return (
    <div className="bg-white border border-[#E2E0D8] rounded-xl p-4 mt-6" data-testid="tp-payout-onboarding-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate_navy flex items-center gap-2">
            <Wallet size={14} className="text-forest"/> Payout account (Stripe Connect)
          </div>
          <div className="text-xs text-slate-500 mt-1">{status.message || (status.onboarded ? "Ready to receive transfers." : "Payout onboarding incomplete.")}</div>
        </div>
        <span className={`px-2 py-1 rounded-full text-[11px] font-semibold ${status.onboarded ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`} data-testid="tp-payout-status-badge">
          {status.onboarded ? "Onboarded" : "Not onboarded"}
        </span>
      </div>
      {!status.onboarded && (
        <button onClick={sendOnboardingLink} disabled={busy} className="mt-3 inline-flex items-center gap-2 bg-forest text-white px-3 py-2 rounded-md text-xs font-semibold disabled:opacity-50" data-testid="tp-payout-send-onboarding">
          {busy ? <Loader2 size={12} className="animate-spin"/> : <Wallet size={12}/>} Send onboarding link
        </button>
      )}
    </div>
  );
}
